import React, { Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { HashLink as Link } from 'react-router-hash-link';

import { ascending } from 'd3-array';
import { select } from 'd3-selection';
import { timeDay } from 'd3-time';

import D3Component from './D3Component';
import DayFilterContext from '../DayFilterContext';
import { getDaysScrollKeyHandler } from './Days';
import GlucoseDayGraph from './GlucoseDayGraph';
import { getRanges } from './GlucoseStatsRow';
import GlucoseSVG from './GlucoseSVG';
import InsulinDistributionSVG from './InsulinDistributionSVG';
import { getInsulinStats } from './InsulinStatsRow';
import PresentationContext from '../PresentationContext';
import TiRBar from './TiRBar';
import TreatmentsOverlay from './TreatmentsOverlay';
import { internalUnits, mmolL } from '../utils/consts';
import { parseDate, urlDateFormat } from '../utils/formats';
import { layout } from '../utils/layoutUtils';
import './Dashboard.css';

function GraphicalDiaryView(props) {
  const {
    day,
    days,
    interactive = true,
    basalData,
    basalExtent,
    bolusData,
    bolusExtent,
    carbExtent,
    // eventData,
    exerciseData,
    // notesData,
    longActingData,
    longActingExtent,
    maxCarbs,
    maxExercise,
    maxSleep,
    maxSteps,
    nutritionData,
    // photosData,
    profilesData,
    sgvData,
    sleepData,
    smbgData,
    stepsData,
    t,
    ticks,
    timeScale,
  } = props;

  const {
    durationFormat,
    formatDate,
    formatDateString,
    formatGlucose,
    formatGlucoseValue,
    formatTimeString,
    weekInterval,
  } = useContext(PresentationContext);

  const { setDays: setContextDays } = useContext(DayFilterContext);

  const setDays = useMemo(() => function setDay(from, to) {
    setContextDays(timeDay.range(from, timeDay.offset(to, 1)).map(urlDateFormat));
  }, [setContextDays]);


  const inputHandler = useMemo(() => getDaysScrollKeyHandler({ days, setDays, weekInterval }),
    [days, setDays, weekInterval]);

  useEffect(() => {
    if (interactive) {
      select(document).on('keydown.scrollDays', inputHandler);
    }
    return () => {
      select(document).on('keydown.scrollDays', null);
    };
  }, [inputHandler, interactive]);

  const activityRef = useRef();
  useLayoutEffect(() => {
    layout(select(activityRef.current).selectAll('.bar'));
  }, [activityRef]);

  const extendedTicks = useMemo(() => {
    const sortedTicks = Object.values(ticks).sort(ascending);
    // Replace max value to include all possible values
    sortedTicks[sortedTicks.length - 1] = internalUnits === 'mmol/l' ? 50 : 1000;
    return sortedTicks;
  }, [ticks]);

  return (
    <section id="diary">
      {days.map(d => {
        const date = day ? parseDate(d) : new Date();

        const previousDay = urlDateFormat(timeDay.offset(date, -1));

        const previousDayBasal = basalData[previousDay];
        const extendedBasalData = [...(basalData[d] || [])];
        if (previousDayBasal && previousDayBasal.length) {
          extendedBasalData.unshift(previousDayBasal[previousDayBasal.length - 1]);
        }

        const previousDayLongActing = longActingData[previousDay];
        const extendedLongActingData = [...(longActingData[d] || [])];
        if (previousDayLongActing && previousDayLongActing.length) {
          extendedLongActingData.unshift(previousDayLongActing[previousDayBasal.length - 1]);
        }

        const previousNightSleep = sleepData[previousDay] || [];
        const extendedSleepData = [...(sleepData[d] || [])];
        if (previousNightSleep && previousNightSleep.length) {
          extendedSleepData.unshift(previousNightSleep[previousNightSleep.length - 1]);
        }

        const basal = [ ...(basalData[d] || []), ...(longActingData[d] || [])];
        const bolus = bolusData[d];

        const { dailyMeans, dailyDeviations } = getInsulinStats(basal, bolus, [d], profilesData);
        const underlay = (
          <D3Component
            basal={extendedBasalData}
            basalExtent={basalExtent}
            longActing={extendedLongActingData}
            longActingExtent={longActingExtent}
            bolus={bolus}
            bolusExtent={bolusExtent}
            carbExtent={carbExtent}
            className="treatments"
            day={d}
            formatTimeString={formatTimeString}
            graph={TreatmentsOverlay}
            nutrition={nutritionData[d]}
            t={t}
            timeScale={timeScale}
          />
        );

        const totalCarbs = nutritionData[d].reduce((t, c) => t + c.carbohydrate, 0);

        const ranges = getRanges({ [d]: sgvData[d] }, extendedTicks);

        const title = `${formatDate(parseDate(d))}:\n${
          ranges.hyper.value.toFixed(0)
        } % > ${
          formatGlucose(250, mmolL)
        }\n${
          ranges.high.value.toFixed(0)
        } % ${
          formatGlucoseValue(250, mmolL)
        }-${
          formatGlucose(180, mmolL)
        }\n${
          ranges.low.value.toFixed(0)
        } % ${
          formatGlucoseValue(180, mmolL)
        }-${
          formatGlucose(70, mmolL)
        }\n${
          ranges.hypo.value.toFixed(0)
        } % ${
          formatGlucoseValue(70, mmolL)
        }-${
          formatGlucose(54, mmolL)
        }\n${
          ranges.min.value.toFixed(0)
        } % < ${
          formatGlucose(54, mmolL)
        }`;

        const carbBarHeight = (totalCarbs / maxCarbs) * 100;
        const carbBarY = 100 - carbBarHeight;
        const invert = totalCarbs > (maxCarbs * 0.8);

        // TODO: check by source, and overlaps, etc.
        // As with the previous version
        const steps = stepsData[d].reduce((t, s) => t + s.steps, 0);
        const exercise = exerciseData[d].reduce((t, e) => t + e.duration, 0);
        const sleep = (previousNightSleep[previousNightSleep.length - 1] || {}).duration || 0;

        return (
          <section key={d} id={`day${d}`}>
            <h3 className="activity ">
              <Link to={`../day/${d}`}>{formatDateString(d)}</Link>
              <span className="weekDay">
                {t(`common.days.${parseDate(d).getDay()}`)}
              </span>
            </h3>
            <GlucoseSVG
              key="mainGraph"
              underlay={underlay}
              times={[date, timeDay.offset(date, 1)]}
            >
              <D3Component
                data={{ sgv: sgvData[d], smbg: smbgData[d] }}
                day={d}
                days={days}
                formatGlucose={formatGlucose}
                graph={GlucoseDayGraph}
                timeScale={timeScale}
              />
            </GlucoseSVG>
            <InsulinDistributionSVG
              dailyDeviations={dailyDeviations}
              dailyMeans={dailyMeans}
              t={t}
            />
            <svg className="carbsBar" viewBox="0 0 46 100">
              <rect className="bg" width="46" height="100" rx="5" />
              {carbBarY < 90
                ? (carbBarY < 10
                  ? (
                    <rect className="value" width="46" height="100" rx="5" />
                  )
                  : (
                    <Fragment>
                      <rect className="value" y="90" width="46" height="10" rx="5" />
                      <rect className="value" y={carbBarY} width="46" height={carbBarHeight - 5} />
                    </Fragment>
                  )
                )
                : undefined
              }
              <text
                className={invert ? 'inverse' : ''}
                x="23"
                y={carbBarY + (invert ? 16 : -5)}
                textAnchor="middle"
              >
                {`${totalCarbs} g`}
              </text>
            </svg>
            <svg className="activity" viewBox="0 0 270 161">
              <g className="steps bar" transform="translate(0,40)">
                <text className="label adjustLayout" dy="-8">{t('diary.steps')}</text>
                {steps
                  ? (
                    <text
                      className="value adjustLayout"
                      dx={Math.max(steps / maxSteps * 265, 85)}
                      dy="-8"
                      textAnchor="end"
                    >
                      {steps}
                    </text>
                  )
                  : undefined
                }
                <line x1="3" x2={Math.max((steps / maxSteps * 265), 3)} />
              </g>
              <g className="exercise bar" transform="translate(0,100)">
                <text className="label adjustLayout" dy="-8">{t('diary.exercise')}</text>
                {exercise
                  ? (
                    <text
                      className="value adjustLayout"
                      dx={Math.max(exercise / maxExercise * 265, 115)}
                      dy="-8"
                      textAnchor="end"
                    >
                      {durationFormat(exercise)}
                    </text>
                  )
                  : undefined
                }
                <line x1="3" x2={Math.max((exercise / maxExercise * 265), 3)} />
              </g>
              <g className="sleep bar" transform="translate(0,158)">
                <text className="label adjustLayout" dy="-8">{t('diary.sleep')}</text>
                {sleep
                  ? (
                    <text
                      className="value adjustLayout"
                      dx={Math.max(sleep / maxSleep * 265, 120)}
                      dy="-8"
                      textAnchor="end"
                    >
                      {durationFormat(sleep / 60)}
                    </text>
                  )
                  : undefined
                }
                <line x1="3" x2={Math.max((sleep / maxSleep * 265), 3)} />
              </g>
            </svg>
            <TiRBar ranges={ranges} showHandles={false} title={title} viewBox="-17 -3 45 106" />
            <data
              className="tir"
              value={ranges.low.value}
              style={{
                paddingBottom: `${(205 - ranges.hypo.y - ranges.low.y) * 0.05}%`,
              }}
            >
              {`${ranges.low.value.toFixed(0)} %`}
            </data>
          </section>
        );
      })}
    </section>
  );
};

export default GraphicalDiaryView;
